import { Spreadsheet } from '@/components/spreadsheet/spreadsheet';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as numeral from 'numeral';

export class DapSpreadsheetInfluencePlan extends Spreadsheet {
  name: string = 'market_influence_plan';

  mount (dataSource: any[]) {
    const totalMonths = dataSource[0].dapValuesFoundCombined.length;
    const initialValue = 11;

    const sections: Array<any> = [
      { key: 'main', text: '', index: 0 },
      { key: 'dapValuesFoundCombined', text: 'DAP COMBINED', index: initialValue },
      { key: 'dapValuesFound', text: 'MONTHLY ANNUAL PLAN', index: initialValue + (totalMonths || 1) }
    ];

    const sectionColumns: any = {
      main: [
        { key: 'dealerNumber', text: 'DN' },
        { key: 'dealerName', text: 'NAME' },
        { key: 'storeType', text: 'TYPE' },
        { key: 'groupName', text: 'GROUP' },
        { key: 'dealerNumberMatrix', text: 'DN MATRIX' },
        { key: 'markedAreaName', text: 'MARKED AREA' },
        { key: 'region', text: 'REGION' },
        { key: 'aov', text: 'AOV' },
        { key: 'regionale', text: 'REGIONAL' },
        { key: 'marketLevel', text: 'MARKET SHARE REFERENCE' },
        { key: 'segmentName', text: 'SEGMENT' }
      ],
      dapValuesFoundCombined: [
        ..._.map(dataSource[0].dapValuesFoundCombined, (d: any) => ({
          key: 'value',
          text: +moment(`${d.year}${d.month}`, 'YYYYMM').toDate() < +moment().startOf('month').toDate() ? 'SALES DONE' : 'SALES GOAL',
          format: '#0.00%',
          color: +moment(`${d.year}${d.month}`, 'YYYYMM').toDate() < +moment().startOf('month').toDate() ? 'green' : 'dapValuesFoundCombined'
        }))
      ],
      dapValuesFound: [
        ..._.map(dataSource[0].dapValuesFound, (d: any) => ({
          key: 'value',
          text: +moment(`${d.year}${d.month}`, 'YYYYMM').toDate() < +moment().startOf('month').toDate() ? 'SALES DONE' : 'SALES GOAL',
          format: '#,0',
          color: +moment(`${d.year}${d.month}`, 'YYYYMM').toDate() < +moment().startOf('month').toDate() ? 'green' : 'dapValuesFound'
        }))
      ]
    };

    const sectionColumnsDateTime: any = {
      main: [
        { key: 'dealerNumber', text: '' }
      ],
      dapValuesFoundCombined: [
        ..._(dataSource[0].dapValuesFoundCombined)
          .map((d: any, i: number) => ({ key: 'year', text: String(d.year), index: initialValue + i }))
          .uniqBy((d: any) => d.text)
          .value()
      ],
      dapValuesFound: [
        ..._(dataSource[0].dapValuesFound)
          .map((d: any, i: number) => ({ key: 'year', text: String(d.year), index: initialValue + (totalMonths || 1) + i }))
          .uniqBy((d: any) => d.text)
          .value()
      ]
    };

    const sectionColumnsDescription: any = {
      main: [
        { key: 'dealerNumber', text: '' }
      ],
      dapValuesFoundCombined: [
        ..._.map(dataSource[0].dapValuesFoundCombined, d => ({ key: 'month', text: moment(numeral(d.month).format('00'), 'MM').format('MMM').toUpperCase() }))
      ],
      dapValuesFound: [
        ..._.map(dataSource[0].dapValuesFound, d => ({ key: 'month', text: moment(numeral(d.month).format('00'), 'MM').format('MMM').toUpperCase() }))
      ]
    };

    const baseStyle: any = {
      textAlign: 'left',
      fontSize: 9,
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseHeaderStyle: any = {
      color: '#212121',
      fontSize: 9,
      bold: true,
      textAlign: 'center',
      verticalAlign: 'center',
      fontFamily: 'Calibri'
    };

    const baseColorStyle: any = {
      main: '#bdbdbd',
      dapValuesFoundCombined: '#e57373',
      dapValuesFound: '#90a4ae',
      grey: '#d1d1d1',
      greyDarken: '#c1c1c1',
      yellow: '#ffb74d',
      green: '#81c784'
    };

    const baseBorderColorStyle: any = {
      main: '#e0e0e0',
      dapValuesFoundCombined: '#ffcdd2',
      dapValuesFound: '#cfd8dc',
      yellow: '#ffe0b2',
      green: '#c8e6c9'
    };

    const baseBackgroundColorStyle: any = {
      main: '#fafafa',
      dapValuesFoundCombined: '#fff5f6',
      dapValuesFound: '#f9fafb',
      yellow: '#fffbf5',
      green: '#f4f9f5'
    };

    const headerStyle = _.reduce(baseColorStyle, (acc: any, val: string, key: string) => {
      acc[key] = _.assign({}, baseHeaderStyle, { background: baseColorStyle[key] });
      return acc;
    }, {});

    const getCellStyle = (key: any) => {
      return _.assign({}, baseStyle, {
        textAlign: 'center',
        background: baseBackgroundColorStyle[key],
        borderLeft: { color: baseBorderColorStyle[key], size: 1 },
        borderRight: { color: baseBorderColorStyle[key], size: 1 },
        borderTop: { color: baseBorderColorStyle[key], size: 1 },
        borderBottom: { color: baseBorderColorStyle[key], size: 1 }
      });
    };

    const dataSourceGroup = _(dataSource)
      .groupBy(d => d.familyName)
      .value();

    this.name = `dap_${_.map(dataSourceGroup, (val: any, key: string) => key.toLowerCase()).join('_')}_${this.name}`;

    this.workbookOptions = {
      sheets: _.map(dataSourceGroup, (data: any, name: string) => (
        {
          name: `INFLUENCE PLAN ${name}`.toUpperCase(),
          frozenColumns: 2,
          frozenRows: 4,
          filter: {
            ref: `A4:${this.columnIndex[initialValue - 1 + (totalMonths * 2)]}${data.length + 4}`,
            columns: []
          },
          rows: [
            {
              cells: [
                ..._.map(sections, (section: any) =>
                  _.assign({}, headerStyle[section.key], {
                    value: section.text,
                    index: section.index,
                    borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                    borderRight: { color: baseBorderColorStyle[section.key], size: 1 }
                  }))
              ]
            },
            {
              cells: [
                ..._(sectionColumnsDateTime)
                  .map((columns: any, key: any) =>
                    _.map(columns, (cell: any) =>
                      _.assign({}, getCellStyle(cell.color || key), {
                        value: cell.text,
                        bold: true,
                        index: cell.index,
                        background: baseColorStyle.grey,
                        borderLeft: { color: baseBorderColorStyle.main, size: 1 },
                        borderRight: { color: baseBorderColorStyle.main, size: 1 },
                        borderTop: { color: baseBorderColorStyle.main, size: 1 },
                        borderBottom: { color: baseBorderColorStyle.main, size: 1 }
                      })))
                  .flatten()
                  .value()
              ]
            },
            {
              cells: [
                ..._(sectionColumnsDescription)
                  .map((columns: any, key: any) =>
                    _.map(columns, (cell: any) =>
                      _.assign({}, getCellStyle(cell.color || key), {
                        value: cell.text,
                        bold: true,
                        background: baseColorStyle.greyDarken,
                        borderLeft: { color: baseBorderColorStyle.main, size: 1 },
                        borderRight: { color: baseBorderColorStyle.main, size: 1 },
                        borderTop: { color: baseBorderColorStyle.main, size: 1 },
                        borderBottom: { color: baseBorderColorStyle.main, size: 1 }
                      })))
                  .flatten()
                  .value()
              ]
            },
            {
              height: 80,
              cells: [
                ..._(sectionColumns)
                  .map((columns: any, key: any) =>
                    _.map(columns, (cell: any) =>
                      _.assign({}, headerStyle[cell.color || key], {
                        value: cell.text,
                        wrap: true,
                        borderLeft: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                        borderRight: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                        borderTop: { color: baseBorderColorStyle[cell.color || key], size: 1 },
                        borderBottom: { color: baseBorderColorStyle[cell.color || key], size: 1 }
                      })))
                  .flatten()
                  .value()
              ]
            },
            ..._.map(data, d => {
              return ({
                cells: [
                  ..._(sectionColumns)
                    .map((columns: any, key: any) =>
                      _.map(columns, (cell: any, index: number) => {
                        if (key === 'dapValuesFoundCombined' || key === 'dapValuesFound') {
                          return _.assign({}, getCellStyle(cell.color || key), ({
                            value: d[key][index][cell.key],
                            format: cell.format
                          }));
                        }
                        return _.assign({}, getCellStyle(cell.color || key), {
                          value: d[cell.key],
                          format: cell.format
                        });
                      }))
                    .flatten()
                    .value()
                ]
              });
            })
          ],
          columns: [
            {
              index: 0,
              width: 100
            },
            {
              index: 1,
              width: 160
            },
            {
              index: 2,
              width: 100
            },
            {
              index: 3,
              width: 100
            },
            {
              index: 4,
              width: 100
            },
            {
              index: 5,
              width: 200
            },
            {
              index: 6,
              width: 120
            },
            ..._.map(_.range(0, dataSource.length - 7), index => ({
              index: index + 7,
              width: 75
            }))
          ],
          showGridLines: false,
          mergedCells: [
            `A1:${this.columnIndex[initialValue - 1]}1`,
            `A2:${this.columnIndex[initialValue - 1]}2`,
            `A3:${this.columnIndex[initialValue - 1]}3`,
            `${this.columnIndex[sections[1].index]}1:${this.columnIndex[sections[2].index - 1]}1`,
            `${this.columnIndex[sections[2].index]}1:${this.columnIndex[sections[2].index + totalMonths - 1]}1`,
            ..._.map(sectionColumnsDateTime.dapValuesFoundCombined, (c: any) => `${this.columnIndex[c.index]}2:${this.columnIndex[c.index - 1 + ((_(dataSource[0].dapValuesFoundCombined).groupBy((d: any) => d.year).find((val: any, key: string) => +key === +c.text) as any).length)]}2`),
            ..._.map(sectionColumnsDateTime.dapValuesFound, (c: any) => `${this.columnIndex[c.index]}2:${this.columnIndex[c.index - 1 + ((_(dataSource[0].dapValuesFound).groupBy((d: any) => d.year).find((val: any, key: string) => +key === +c.text) as any).length)]}2`)
          ]
        }
      ) as any)
    };

    return this;
  }
}
